import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { FC, useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { ActionButton } from '@/pages/MLProject/components/Card';
import {
  ColumnTitle,
  Editor,
  EditorFooterActions,
  Row,
  ScriptColumn,
  ScriptFieldWrap,
  TimestampsColumn,
} from '@/pages/MLProject/components/ScriptsCard';
import {
  MLProjectScriptPart,
  MLProjectTranslation,
  MLProjectTranslationScriptPart,
} from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

import ScriptField from './ScriptField';
import SubsPositionField from './SubsPositionField';
import TimestampField from './TimestampField';
import VariationSwitchField from './VariationSwitchField';

const TypeLabel = styled(Text)`
  margin-bottom: 16px;
`;

const Translations = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const getSubtitlesBoxDimensions = (text: string) => {
  const el = document.createElement('div');

  el.innerHTML = text.replaceAll('\\N', '<br/>');

  el.style.fontSize = '32px';
  el.style.padding = '15px 0';
  el.style.fontWeight = '600';
  el.style.display = 'inline-block';

  document.body.appendChild(el);

  const positionInfo = el.getBoundingClientRect();

  document.body.removeChild(el);

  return {
    width: Math.floor(positionInfo.width),
    height: Math.floor(positionInfo.height),
  };
};

interface Props {
  translationId: number;
  originalPart: MLProjectScriptPart;
  part: MLProjectTranslationScriptPart;
}

export type FormDataT = {
  timestamp: string;
  text: {
    a: string;
    b: string;
  };
  subtitlesPosition: 'bottom' | 'top';
  selectedTranslation: keyof FormDataT['text'] | '';
  splittedPart?: {
    timestamp: string;
    text: string;
  };
};

const ScriptPart: FC<Props> = ({ translationId, originalPart, part }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    clearErrors,
    reset,
    formState: { isSubmitting, isDirty, defaultValues },
  } = useForm<FormDataT>({
    defaultValues: {
      timestamp: part.timestamp,
      text: {
        a: part.text.a ?? '',
        b: part.text.b ?? '',
      },
      subtitlesPosition: part.subtitlesPosition ?? 'bottom',
      selectedTranslation: part.selectedTranslation ?? '',
      splittedPart: part.splittedPart,
    },
  });

  const isSaveButtonDisabled =
    !isDirty || isSubmitting || part.isGeneratingVoiceover;

  const onSubmit = useCallback(
    (useAlerts = false) => {
      clearErrors();
      handleSubmit(async (data) => {
        try {
          const selectedTranslation = (data.selectedTranslation ||
            'a') as keyof (typeof data)['text'];

          const res = await api.put<object, MLProjectTranslationScriptPart>(
            `/v1/distribution/ml-translations/${translationId}/parts`,
            {
              uuid: part.uuid,
              ...data,
              htmlBox: getSubtitlesBoxDimensions(
                data.text[selectedTranslation],
              ),
            },
          );

          queryClient.setQueryData(
            [
              'ml_translations',
              {
                id: translationId.toString(),
              },
            ],
            (translation: MLProjectTranslation | undefined) => {
              if (!translation) {
                return undefined;
              }

              return {
                ...translation,
                script: translation.script!.map((part) => {
                  if (part.uuid !== res.uuid) {
                    return part;
                  }

                  return res;
                }),
              };
            },
          );

          reset({
            timestamp: res.timestamp,
            text: res.text,
            subtitlesPosition: res.subtitlesPosition ?? 'bottom',
            selectedTranslation: res.selectedTranslation ?? '',
            splittedPart: res.splittedPart,
          });

          toasts.create('success', 'Saved');
        } catch (e) {
          handleFormErrors(e, setError, useAlerts ? alerts : undefined, true);
        }
      })();
    },
    [
      clearErrors,
      queryClient,
      reset,
      toasts,
      alerts,
      setError,
      handleSubmit,
      part.uuid,
      translationId,
    ],
  );

  // eslint-disable-next-line
  const debouncedSave = useCallback(debounce(onSubmit, 3000), [onSubmit]);

  const watchedData = useWatch<FormDataT>({
    control,
    defaultValue: defaultValues,
  });

  const appendSubPart = (text: string) => {
    setValue('splittedPart', {
      timestamp: part.timestamp,
      text,
    });
  };

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    debouncedSave();
  }, [watchedData, isDirty, debouncedSave]);

  return (
    <Editor id={`ml-script-part-${part.uuid}`}>
      <Row>
        <TimestampsColumn>
          <ColumnTitle>Timestamps</ColumnTitle>
        </TimestampsColumn>
        <ScriptColumn>
          <ColumnTitle>Script</ColumnTitle>
        </ScriptColumn>
      </Row>
      <Row>
        <TimestampsColumn>
          <Text $type="caption" $color="gray">
            {originalPart.timestamp}
          </Text>
        </TimestampsColumn>
        <ScriptColumn>
          <TypeLabel $type="smallBold" $color="gray">
            Original
          </TypeLabel>
          <Text $type="body">{originalPart.text}</Text>
        </ScriptColumn>
      </Row>
      <Row>
        <TimestampsColumn>
          <TimestampField control={control} />
        </TimestampsColumn>
        <ScriptColumn>
          <TypeLabel $type="smallBold" $color="gray">
            Translation
          </TypeLabel>
          <Translations>
            <ScriptFieldWrap>
              <VariationSwitchField control={control} variation="a" />
              <ScriptField
                control={control}
                name="text.a"
                activeVariation={part.selectedTranslation}
                appendSubPart={appendSubPart}
              />
              <SubsPositionField control={control} />
            </ScriptFieldWrap>
            <ScriptFieldWrap>
              <VariationSwitchField control={control} variation="b" />
              <ScriptField
                control={control}
                name="text.b"
                activeVariation={part.selectedTranslation}
                appendSubPart={appendSubPart}
              />
            </ScriptFieldWrap>
          </Translations>
          {!watchedData.splittedPart && (
            <EditorFooterActions>
              <ActionButton
                onClick={() => onSubmit(true)}
                disabled={isSaveButtonDisabled}
              >
                Save
              </ActionButton>
            </EditorFooterActions>
          )}
        </ScriptColumn>
      </Row>
      {!!watchedData.splittedPart && (
        <Row>
          <TimestampsColumn>
            <TimestampField control={control} name="splittedPart.timestamp" />
          </TimestampsColumn>
          <ScriptColumn>
            <TypeLabel $type="smallBold" $color="gray">
              Split from {watchedData.selectedTranslation?.toUpperCase()}
            </TypeLabel>
            <Translations>
              <ScriptFieldWrap>
                <ScriptField
                  control={control}
                  name="splittedPart.text"
                  activeVariation={part.selectedTranslation}
                  appendSubPart={appendSubPart}
                  removeSubPart={() => setValue('splittedPart', undefined)}
                />
              </ScriptFieldWrap>
            </Translations>
            <EditorFooterActions>
              <ActionButton
                onClick={() => onSubmit(true)}
                disabled={isSaveButtonDisabled}
              >
                Save
              </ActionButton>
            </EditorFooterActions>
          </ScriptColumn>
        </Row>
      )}
    </Editor>
  );
};

export default ScriptPart;
